import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, BookOpen, CheckCircle2, GraduationCap, Loader2, MessageCircleQuestion, PauseCircle,
    RefreshCw, Save, ThumbsDown, ThumbsUp,
} from 'lucide-react';
import { kapitelNachId } from '../../tutorial/register';
import {
    FRAGEN_LIMIT,
    ladeFragen,
    ladeKapitelVerwaltung,
    setzeKapitelStatus,
    speichereHinweis,
} from '../../services/tutorial/tutorialAdminService';
import type {
    KapitelStatus, TutorialFrageZeile, TutorialKapitelZeile,
} from '../../services/tutorial/tutorialAdminService';

/**
 * VERWALTUNG → Reiter „Lernzentrum" (Plan §1.6 und §1.8, Paket E).
 *
 * Zwei Listen, ein Zweck: den Lernstoff ehrlich halten.
 *
 *  - **Fragen** aus `tutorial_fragen`, neueste zuerst. Der Haken „nur ohne
 *    Kapitel" macht daraus die LUECKENLISTE: Fragen, zu denen die Hilfe nichts
 *    hergab. Jede Zeile darin ist ein Auftrag fuer ein neues Registerkapitel —
 *    und sie steht hier, weil niemand von selbst meldet, was er nicht gefunden
 *    hat.
 *  - **Kapitel** aus `tutorial_kapitel` mit Status und Fassung. Der Text ist
 *    KI-erzeugt; nichts davon erreicht einen Nutzer, bevor ein Mensch
 *    „Freigeben" gedrueckt hat. Zurueckstellen geht jederzeit und nimmt das
 *    Kapitel sofort wieder aus der App.
 *
 * Duenner Container: der Datenzugriff liegt in `tutorialAdminService.ts` und
 * ist fail-open. Fehlt die Migration (Nr. 100/101), stehen hier zwei leere
 * Listen mit einem erklaerenden Satz — keine rote Meldung.
 */

const datum = (iso?: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
};

const STATUS_TEXT: Record<KapitelStatus, string> = {
    entwurf: 'Entwurf',
    freigegeben: 'Freigegeben',
    zurueckgestellt: 'Zurückgestellt',
};

const STATUS_KLASSE: Record<KapitelStatus, string> = {
    entwurf: 'bg-slate-100 text-slate-600 border-slate-200',
    freigegeben: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    zurueckgestellt: 'bg-amber-50 text-amber-700 border-amber-200',
};

/** Titel aus dem Register; die Datenbank kann bei einem geloeschten Kapitel schweigen. */
const kapitelTitel = (id: string): string => kapitelNachId(id)?.titel ?? id;

export const AdminLernzentrumView: React.FC = () => {
    const [fragen, setFragen] = useState<TutorialFrageZeile[]>([]);
    const [kapitel, setKapitel] = useState<TutorialKapitelZeile[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [nurLuecken, setNurLuecken] = useState(false);
    const [arbeitetAn, setArbeitetAn] = useState<string | null>(null);
    const [meldung, setMeldung] = useState<string | null>(null);
    /** Offener Hinweis-Editor je Kapitel-Id; `undefined` heisst „nicht in Bearbeitung". */
    const [hinweisEntwurf, setHinweisEntwurf] = useState<Record<string, string>>({});

    /**
     * Erstes Laden. Kein `setLaedt(true)` im Effektrumpf: `laedt` startet auf
     * `true`, und ein synchrones setState im Effekt erzeugt eine
     * Kaskadenrendierung (`react-hooks/set-state-in-effect`, CI-Ratsche).
     * Beide Abrufe zusammen — sie haengen nicht voneinander ab, und
     * nacheinander waere es der doppelte Rundlauf bis zum ersten Bild.
     */
    useEffect(() => {
        let abgebrochen = false;
        void Promise.all([ladeFragen(FRAGEN_LIMIT), ladeKapitelVerwaltung()]).then(([f, k]) => {
            if (abgebrochen) return;
            setFragen(f);
            setKapitel(k);
            setLaedt(false);
        });
        return () => { abgebrochen = true; };
    }, []);

    /** Knopf „Aktualisieren" — ein Ereignis, kein Effekt: hier darf der Spinner an. */
    const neuLaden = useCallback(async () => {
        setLaedt(true);
        const [f, k] = await Promise.all([ladeFragen(FRAGEN_LIMIT), ladeKapitelVerwaltung()]);
        setFragen(f);
        setKapitel(k);
        setLaedt(false);
    }, []);

    const sichtbareFragen = useMemo(
        () => (nurLuecken ? fragen.filter((f) => f.kapitelIds.length === 0) : fragen),
        [fragen, nurLuecken],
    );
    const luecken = useMemo(() => fragen.filter((f) => f.kapitelIds.length === 0).length, [fragen]);

    const statusUmlegen = async (id: string, status: KapitelStatus) => {
        setArbeitetAn(id);
        setMeldung(null);
        const ok = await setzeKapitelStatus(id, status);
        if (ok) {
            setKapitel((alt) => alt.map((k) => (k.id === id
                ? {
                    ...k,
                    status,
                    freigegebenAm: status === 'freigegeben' ? new Date().toISOString() : null,
                }
                : k)));
        } else {
            setMeldung('Der Status konnte nicht gespeichert werden. Bitte Seite neu laden und noch einmal versuchen.');
        }
        setArbeitetAn(null);
    };

    const hinweisSichern = async (id: string) => {
        const wert = hinweisEntwurf[id] ?? '';
        setArbeitetAn(id);
        setMeldung(null);
        const ok = await speichereHinweis(id, wert);
        if (ok) {
            setKapitel((alt) => alt.map((k) => (k.id === id ? { ...k, hinweisAdmin: wert.trim() || null } : k)));
            setHinweisEntwurf((alt) => {
                const neu = { ...alt };
                delete neu[id];
                return neu;
            });
        } else {
            setMeldung('Der Hinweis konnte nicht gespeichert werden.');
        }
        setArbeitetAn(null);
    };

    return (
        <div className="space-y-6">

            {/* ── Kopf ── */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                        <GraduationCap className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-800">Lernzentrum</h2>
                        <p className="text-[11px] text-slate-500">
                            Fragen der Nutzer und Freigabe der Kapitel.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => void neuLaden()}
                    disabled={laedt}
                    className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-600 text-xs font-semibold px-3 py-2 rounded-xl border border-slate-200 transition-colors disabled:opacity-50"
                >
                    {laedt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Aktualisieren
                </button>
            </div>

            {meldung && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-800">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />{meldung}
                </div>
            )}

            {/* ── Fragen / Lückenliste ── */}
            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <MessageCircleQuestion className="w-4 h-4 text-slate-400" />
                        <h3 className="text-sm font-bold text-slate-700">
                            Fragen <span className="text-slate-400 font-semibold">(letzte {FRAGEN_LIMIT})</span>
                        </h3>
                        {luecken > 0 && (
                            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5">
                                {luecken} ohne Kapitel
                            </span>
                        )}
                    </div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={nurLuecken}
                            onChange={(e) => setNurLuecken(e.target.checked)}
                            className="rounded border-slate-300"
                        />
                        Nur ohne Kapitel (Lückenliste)
                    </label>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] font-black uppercase tracking-wider text-slate-400 bg-slate-50">
                                <th className="text-left px-4 py-2 whitespace-nowrap">Zeit</th>
                                <th className="text-left px-4 py-2 whitespace-nowrap">Ansicht</th>
                                <th className="text-left px-4 py-2">Frage</th>
                                <th className="text-left px-4 py-2">Kapitel</th>
                                <th className="text-left px-4 py-2 whitespace-nowrap">Hilfreich</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sichtbareFragen.map((f) => (
                                <tr key={f.id} className="border-t border-slate-100 align-top">
                                    <td className="px-4 py-2.5 text-[11px] text-slate-500 whitespace-nowrap">{datum(f.createdAt)}</td>
                                    <td className="px-4 py-2.5 text-[11px] text-slate-500 whitespace-nowrap">{f.view || '—'}</td>
                                    <td className="px-4 py-2.5 text-slate-700 max-w-md">
                                        <p className="leading-snug">{f.frage}</p>
                                        {f.antwort && (
                                            <p className="text-[11px] text-slate-400 leading-snug mt-1 line-clamp-2">{f.antwort}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5">
                                        {f.kapitelIds.length === 0 ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5">
                                                <AlertTriangle className="w-3 h-3" />Lücke
                                            </span>
                                        ) : (
                                            <div className="flex flex-wrap gap-1">
                                                {f.kapitelIds.map((id) => (
                                                    <span
                                                        key={id}
                                                        title={id}
                                                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-1.5 py-0.5"
                                                    >
                                                        <BookOpen className="w-3 h-3" />{kapitelTitel(id)}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 whitespace-nowrap">
                                        {f.hilfreich === true && <ThumbsUp className="w-3.5 h-3.5 text-emerald-600" />}
                                        {f.hilfreich === false && <ThumbsDown className="w-3.5 h-3.5 text-red-500" />}
                                        {f.hilfreich === null && <span className="text-[11px] text-slate-300">—</span>}
                                    </td>
                                </tr>
                            ))}
                            {sichtbareFragen.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                                        {laedt
                                            ? 'Wird geladen …'
                                            : nurLuecken
                                                ? 'Keine Frage ohne passendes Kapitel — die Hilfe deckt ab, was gefragt wurde.'
                                                : 'Noch keine Fragen. Sobald der Chat benutzt wird, stehen sie hier.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ── Kapitel und Freigabe ── */}
            <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-slate-400" />
                    <h3 className="text-sm font-bold text-slate-700">Kapitel</h3>
                    <span className="text-[11px] text-slate-400">
                        Nur freigegebene Kapitel erreichen die Nutzer.
                    </span>
                </div>

                <div className="divide-y divide-slate-100">
                    {kapitel.map((k) => {
                        const entwurf = hinweisEntwurf[k.id];
                        const inArbeit = arbeitetAn === k.id;
                        return (
                            <div key={k.id} className="px-4 py-3">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-semibold text-slate-700">{k.titel}</span>
                                            <span className={`text-[10px] font-bold border rounded-md px-1.5 py-0.5 ${STATUS_KLASSE[k.status]}`}>
                                                {STATUS_TEXT[k.status]}
                                            </span>
                                            <span className="text-[10px] text-slate-400">Fassung {k.version}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            {k.id}{k.view ? ` · Ansicht ${k.view}` : ''}
                                            {k.freigegebenAm ? ` · freigegeben ${datum(k.freigegebenAm)}` : ''}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        {k.status === 'freigegeben' ? (
                                            <button
                                                type="button"
                                                onClick={() => void statusUmlegen(k.id, 'zurueckgestellt')}
                                                disabled={inArbeit}
                                                className="inline-flex items-center gap-1.5 bg-white hover:bg-amber-50 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-xl border border-amber-200 transition-colors disabled:opacity-50"
                                            >
                                                {inArbeit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PauseCircle className="w-3.5 h-3.5" />}
                                                Zurückstellen
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => void statusUmlegen(k.id, 'freigegeben')}
                                                disabled={inArbeit}
                                                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-xl border border-emerald-600 transition-colors disabled:opacity-50"
                                            >
                                                {inArbeit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                                Freigeben
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* ── Hinweis der Verwaltung ──
                                    Er ueberlebt die Regenerierung durch die Pipeline und ist die
                                    Stelle, an der steht, WARUM ein Kapitel zurueckgestellt wurde. */}
                                <div className="mt-2 flex items-start gap-2">
                                    <textarea
                                        value={entwurf ?? k.hinweisAdmin ?? ''}
                                        onChange={(e) => setHinweisEntwurf((alt) => ({ ...alt, [k.id]: e.target.value }))}
                                        rows={1}
                                        placeholder="Hinweis der Verwaltung (optional) …"
                                        aria-label={`Hinweis zu ${k.titel}`}
                                        className="flex-1 resize-y text-xs text-slate-600 placeholder:text-slate-400 border border-slate-200 rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                                    />
                                    {entwurf !== undefined && entwurf !== (k.hinweisAdmin ?? '') && (
                                        <button
                                            type="button"
                                            onClick={() => void hinweisSichern(k.id)}
                                            disabled={inArbeit}
                                            className="shrink-0 inline-flex items-center gap-1.5 bg-white hover:bg-slate-50 text-slate-600 text-xs font-semibold px-2.5 py-1.5 rounded-xl border border-slate-200 transition-colors disabled:opacity-50"
                                        >
                                            <Save className="w-3.5 h-3.5" />Sichern
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {kapitel.length === 0 && (
                        <p className="px-4 py-8 text-center text-sm text-slate-500">
                            {laedt
                                ? 'Wird geladen …'
                                : 'Noch keine Kapitel in der Datenbank. Der Lernstoff steht bis dahin aus dem Register in der App — freizugeben gibt es erst nach dem ersten Pipeline-Lauf etwas.'}
                        </p>
                    )}
                </div>
            </section>
        </div>
    );
};
